/**
 * @file schemas.ts
 * @brief Zod shapes for the connector API.
 *
 * @details
 * `passthrough()` throughout: the backend adds fields as connectors grow, and a
 * strict schema would turn an additive server change into a broken settings
 * page. Everything optional/nullable for the same reason — a connector that is
 * not configured returns far less than one that is.
 *
 * @references
 * - BACKEND/src/features/connectors/routes.js
 *
 * @author DES
 * @date 05-08-2026
 */

import { z } from "zod";

export const AsanaConnectionSchema = z
  .object({
    connected: z.boolean().default(false),
    account: z.string().nullable().optional(),
    workspace: z.string().nullable().optional(),
    projectId: z.string().nullable().optional(),
    projectName: z.string().nullable().optional(),
    /** "company" — one destination for everyone; "personal" — this account only. */
    scope: z.enum(["company", "personal"]).nullable().optional(),
    needsReconnect: z.boolean().optional(),
    lastError: z.string().nullable().optional(),
  })
  .passthrough();

export const ConnectorStatusSchema = z
  .object({
    success: z.boolean().optional(),
    /** Server has Asana credentials at all. */
    configured: z.boolean().default(false),
    /** Server can encrypt tokens; without it connecting is refused outright. */
    secretStorageReady: z.boolean().default(false),
    asana: AsanaConnectionSchema.default({ connected: false }),
    /** ADR-051 (21-10-2026): false = the company admin has not allowed Asana
     *  while sharing is inside-only. Absent on older servers = allowed. */
    allowedByCompany: z.boolean().optional(),
    blockedMessage: z.string().nullable().optional(),
  })
  .passthrough();

export const AsanaProjectsSchema = z
  .object({
    projects: z
      .array(z.object({ id: z.string(), name: z.string().nullable().optional() }))
      .default([]),
  })
  .passthrough();

export const AsanaWorkspacesSchema = z
  .object({
    workspaces: z
      .array(z.object({ id: z.string(), name: z.string().nullable().optional() }))
      .default([]),
    /** What the connection is pointed at now — the picker's current value. */
    selectedId: z.string().nullable().optional(),
  })
  .passthrough();

export const AsanaPersonSchema = z
  .object({
    userId: z.string(),
    name: z.string().nullable().optional(),
    email: z.string().nullable().optional(),
    /** Signed by the API — the stored path is not usable in a browser. */
    avatarUrl: z.string().nullable().optional(),
    asanaUserId: z.string().nullable().optional(),
    asanaUserName: z.string().nullable().optional(),
    /** How the match was made — `none` is the only one needing a human. */
    source: z.enum(["manual", "email", "none"]).default("none"),
  })
  .passthrough();

export const AsanaPeopleSchema = z
  .object({
    people: z.array(AsanaPersonSchema).default([]),
    members: z
      .array(
        z.object({
          id: z.string(),
          name: z.string().nullable().optional(),
          email: z.string().nullable().optional(),
        }),
      )
      .default([]),
  })
  .passthrough();

export const AsanaPushResultSchema = z
  .object({
    success: z.boolean().optional(),
    pushed: z.number().default(0),
    updated: z.number().default(0),
    skipped: z.number().default(0),
    failed: z.number().default(0),
    /** Ticked in Asana since the last sync and mirrored back here. */
    pulled: z.number().optional(),
    code: z.string().optional(),
    tasks: z
      .array(
        z
          .object({
            localId: z.string().optional(),
            externalId: z.string().optional(),
            url: z.string().nullable().optional(),
            action: z.string().optional(),
            error: z.string().optional(),
          })
          .passthrough(),
      )
      .default([]),
  })
  .passthrough();

export type AsanaConnection = z.infer<typeof AsanaConnectionSchema>;
export type AsanaPerson = z.infer<typeof AsanaPersonSchema>;
export type AsanaPushResult = z.infer<typeof AsanaPushResultSchema>;
